import { collection, getDocs, addDoc, doc, getDoc, setDoc, serverTimestamp, query, orderBy, updateDoc, deleteDoc, increment, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';

const safeDocs = async (colRef) => {
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
};

export const getCoursesFs = async () => {
  const colRef = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const addCourseFs = async (course) => {
  const payload = { ...course, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'courses'), payload);
  return { id: docRef.id, ...course };
};

export const updateCourseFs = async (courseId, updates) => {
  await updateDoc(doc(db, 'courses', courseId), { ...updates, updatedAt: serverTimestamp() });
};

export const removeCourseFs = async (courseId) => {
  await deleteDoc(doc(db, 'courses', courseId));
};

export const getResourcesFs = async () => {
  const colRef = query(collection(db, 'resources'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const addResourceFs = async (resource) => {
  const payload = { ...resource, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'resources'), payload);
  return { id: docRef.id, ...resource };
};

export const updateResourceFs = async (resourceId, updates) => {
  await updateDoc(doc(db, 'resources', resourceId), { ...updates, updatedAt: serverTimestamp() });
};

export const removeResourceFs = async (resourceId) => {
  await deleteDoc(doc(db, 'resources', resourceId));
};

export const getAssignmentsFs = async () => {
  const colRef = query(collection(db, 'assignments'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const addAssignmentFs = async (assignment) => {
  const payload = { ...assignment, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'assignments'), payload);
  return { id: docRef.id, ...assignment };
};

export const updateAssignmentFs = async (assignmentId, updates) => {
  await updateDoc(doc(db, 'assignments', assignmentId), { ...updates, updatedAt: serverTimestamp() });
};

export const getAnnouncementsFs = async () => {
  const colRef = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const addAnnouncementFs = async (announcement) => {
  const payload = { ...announcement, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'announcements'), payload);
  return { id: docRef.id, ...announcement };
};

export const updateAnnouncementFs = async (announcementId, updates) => {
  await updateDoc(doc(db, 'announcements', announcementId), { ...updates, updatedAt: serverTimestamp() });
};

export const getFeesFs = async () => {
  const docRef = doc(db, 'settings', 'fees');
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : null;
};

export const saveFeesFs = async (fees) => {
  const docRef = doc(db, 'settings', 'fees');
  await setDoc(docRef, { ...fees, updatedAt: serverTimestamp() }, { merge: true });
  return fees;
};

export const getPublicSiteFs = async () => {
  const docRef = doc(db, 'settings', 'publicSite');
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : null;
};

export const savePublicSiteFs = async (payload) => {
  const docRef = doc(db, 'settings', 'publicSite');
  await setDoc(docRef, { ...payload, updatedAt: serverTimestamp() }, { merge: true });
  return payload;
};

export const getAdsFs = async () => {
  const colRef = query(collection(db, 'ads'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const addAdFs = async (ad) => {
  const { id: _ignored, ...rest } = ad || {};
  const payload = { ...rest, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'ads'), payload);
  return { id: docRef.id, ...rest };
};

export const removeAdFs = async (adId) => {
  await deleteDoc(doc(db, 'ads', adId));
};

export const addSubmissionFs = async (submission) => {
  const payload = { ...submission, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'submissions'), payload);
  return { id: docRef.id, ...submission };
};

export const updateSubmissionFs = async (submissionId, updates) => {
  await updateDoc(doc(db, 'submissions', submissionId), { ...updates, updatedAt: serverTimestamp() });
  return { id: submissionId, ...updates };
};

export const addMarkFs = async (mark) => {
  const payload = { ...mark, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'marks'), payload);
  return { id: docRef.id, ...mark };
};

export const addUserProfileFs = async (userId, profile) => {
  await setDoc(doc(db, 'users', userId), { ...profile, updatedAt: serverTimestamp() }, { merge: true });
  return { id: userId, ...profile };
};

export const addUserFs = async (profile) => {
  const payload = { ...profile, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'users'), payload);
  return { id: docRef.id, ...profile };
};

export const getUsersFs = async () => {
  const colRef = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const removeUserFs = async (userId) => {
  await deleteDoc(doc(db, 'users', userId));
};

export const addLeadFs = async (lead) => {
  const payload = { ...lead, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'leads'), payload);
  return { id: docRef.id, ...lead };
};

export const updateLeadFs = async (leadId, updates) => {
  await updateDoc(doc(db, 'leads', leadId), { ...updates, updatedAt: serverTimestamp() });
  return { id: leadId, ...updates };
};

export const removeLeadFs = async (leadId) => {
  await deleteDoc(doc(db, 'leads', leadId));
};

export const getLeadByIdFs = async (leadId) => {
  const snap = await getDoc(doc(db, 'leads', leadId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getLeadsFs = async () => {
  const colRef = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const addAutomationEventFs = async (event) => {
  const payload = { ...event, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'automationEvents'), payload);
  return { id: docRef.id, ...event };
};

export const removeAutomationEventFs = async (eventId) => {
  await deleteDoc(doc(db, 'automationEvents', eventId));
};

export const getAutomationEventsFs = async () => {
  const colRef = query(collection(db, 'automationEvents'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};

export const addMessageFs = async (message) => {
  const payload = { ...message, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'messages'), payload);
  return { id: docRef.id, ...message };
};

export const addCommunityMessageFs = async (message) => {
  const payload = { ...message, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'communityMessages'), payload);
  return { id: docRef.id, ...message };
};

export const removeCommunityMessageFs = async (messageId) => {
  await deleteDoc(doc(db, 'communityMessages', messageId));
};

export const updateMessageFs = async (messageId, updates) => {
  await updateDoc(doc(db, 'messages', messageId), { ...updates, updatedAt: serverTimestamp() });
  return { id: messageId, ...updates };
};

export const updateGamificationFs = async (userId, { pointsDelta = 0, badge, name } = {}) => {
  const updates = { updatedAt: serverTimestamp() };
  if (name) updates.name = name;
  if (pointsDelta) updates.points = increment(pointsDelta);
  if (badge) updates.badges = arrayUnion(badge);
  await setDoc(doc(db, 'gamification', userId), updates, { merge: true });
  return { id: userId, ...updates };
};

export const getMillionairesConnectProgressFs = async (userId) => {
  if (!userId) return {};
  const snap = await getDoc(doc(db, 'gamification', userId));
  return snap.exists() ? (snap.data()?.millionairesConnectProgress || {}) : {};
};

export const saveMillionairesConnectProgressFs = async (userId, completedLessonIds = []) => {
  if (!userId) return null;
  const completed = [...new Set(completedLessonIds.filter(Boolean))];
  await setDoc(doc(db, 'gamification', userId), {
    millionairesConnectProgress: {
      completedLessonIds: completed,
      updatedAt: serverTimestamp()
    },
    updatedAt: serverTimestamp()
  }, { merge: true });
  return { completedLessonIds: completed };
};

export const addCalendarEventFs = async (event) => {
  const payload = { ...event, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'calendar'), payload);
  return { id: docRef.id, ...event };
};

export const addPopFs = async (pop) => {
  const payload = { ...pop, createdAt: serverTimestamp() };
  const docRef = await addDoc(collection(db, 'pops'), payload);
  return { id: docRef.id, ...pop };
};

export const updatePopFs = async (popId, updates) => {
  await updateDoc(doc(db, 'pops', popId), { ...updates, updatedAt: serverTimestamp() });
  return { id: popId, ...updates };
};

export const getPopsFs = async () => {
  const colRef = query(collection(db, 'pops'), orderBy('createdAt', 'desc'));
  return safeDocs(colRef);
};
